/**
 * dropbox_read - Read file content from user's Dropbox
 * 
 * Fixes Bug #11: Comprehensive file path validation
 * Fixes Bug #12: Use shared utilities
 */

import { SimpleDocCommand } from '../DocsApp/commands/v5/types';
import { StandardToolResult } from '../../types/standardToolResults';
import { CommandExecutionContext } from '../../types/commands';
import { dropboxService } from '../../services/dropboxService';
import { formatFileSize, validateFilePath } from '../../utils/fileUtils';

export const dropbox_read: SimpleDocCommand = {
  id: 'dropbox_read',
  logicalName: 'dropbox_read',
  version: 'v1',
  
  description: `Read file content from user's Dropbox.

**PREREQUISITES:**
User must have connected their Dropbox account in Settings → Integrations → Dropbox.

**SUPPORTED FILES:**
Text files only:
- Documents: .txt, .md, .rtf
- Code: .js, .ts, .py, .java, .c, .cpp, .go, .rs, .rb, .php, etc.
- Data: .json, .xml, .yaml, .yml, .csv, .tsv
- Config: .ini, .toml, .env, .conf

Binary files (images, PDFs, videos) will return metadata only without content.

**FILE PATH FORMAT:**
- Must start with "/" (e.g., "/Documents/notes.txt")
- Case-sensitive
- Use exact path from dropbox_search results

**EXAMPLES:**

Read a text file:
{ "filePath": "/Documents/meeting-notes.txt" }

Read a JSON config:
{ "filePath": "/config/settings.json" }

Read with custom size limit:
{ "filePath": "/logs/app.log", "maxSizeMB": 5 }

Read from nested folder:
{ "filePath": "/Projects/2024/Q1/report.md" }

**LIMITS:** 
- Default max file size: 10MB
- Can be increased up to 50MB with maxSizeMB parameter
- Binary files return metadata only

**WORKFLOW:**
1. Use dropbox_search to find files
2. Use dropbox_read with the returned path to read content
`,

  schema: {
    parameters: [
      {
        name: 'filePath',
        type: 'string',
        description: 'Full path to file in Dropbox. Must start with "/" (e.g., "/Documents/file.txt")',
        required: true,
        validator: (value: string) => {
          // Use shared validation function (Fix Bug #11)
          return validateFilePath(value);
        }
      },
      {
        name: 'maxSizeMB',
        type: 'number',
        description: 'Maximum file size to read in MB (default: 10, max: 50)',
        required: false,
        validator: (value: number) => {
          if (!value) return null;
          if (value < 0.1 || value > 50) {
            return 'maxSizeMB must be between 0.1 and 50';
          }
          return null;
        }
      }
    ]
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { filePath, maxSizeMB = 10 } = params;
    
    console.log('[dropbox_read] Reading file:', { filePath, maxSizeMB });
    
    try {
      // Check if Dropbox is connected
      const status = await dropboxService.getStatus();
      
      if (!status.connected) {
        return {
          status: 'error',
          message: 'Dropbox not connected',
          error: {
            code: 'not_connected',
            category: 'validation',
            details: 'Please connect your Dropbox account in Settings → Integrations → Dropbox.'
          },
          format: 'plaintext'
        };
      }
      
      // Read file
      const result = await dropboxService.readFile({
        filePath,
        maxSizeMB
      });
      
      const file = result.file;
      
      // Check if content is readable
      if (!file.isText) {
        return {
          status: 'success',
          message: `File "${file.name}" is binary (cannot display content as text)`,
          data: {
            file: {
              id: file.id,
              name: file.name,
              path: file.path,
              size: file.size,
              sizeFormatted: formatFileSize(file.size),
              modifiedAt: file.modifiedAt,
              mimeType: file.mimeType,
              isText: false,
              encoding: file.encoding
            },
            warning: 'Binary files cannot be read as text. Only metadata is returned.',
            hint: 'This file type is not supported for reading. Supported types: .txt, .md, .json, code files, etc.'
          },
          format: 'json'
        };
      }
      
      // Return text content
      const lines = file.content?.split('\n').length || 0;
      const words = file.content?.split(/\s+/).length || 0;
      
      return {
        status: 'success',
        message: `Read file "${file.name}" (${formatFileSize(file.size)}, ${lines} lines)`,
        data: {
          file: {
            id: file.id,
            name: file.name,
            path: file.path,
            size: file.size,
            sizeFormatted: formatFileSize(file.size),
            modifiedAt: file.modifiedAt,
            mimeType: file.mimeType,
            isText: true,
            encoding: file.encoding
          },
          content: file.content,
          stats: {
            characters: file.content?.length || 0,
            words: words,
            lines: lines,
            size: file.size
          }
        },
        format: 'json'
      };
      
    } catch (error: any) {
      console.error('[dropbox_read] Error:', error);
      
      // Handle specific error cases
      const errorMessage = error.response?.data?.detail || error.message;
      
      if (error.response?.status === 404) {
        return {
          status: 'error',
          message: 'Dropbox not connected or file not found',
          error: {
            code: 'not_found',
            category: 'execution',
            details: errorMessage
          },
          format: 'plaintext'
        };
      }
      
      if (errorMessage.includes('too large')) {
        return {
          status: 'error',
          message: 'File too large',
          error: {
            code: 'file_too_large',
            category: 'validation',
            details: errorMessage + ' Try increasing maxSizeMB parameter.'
          },
          format: 'plaintext'
        };
      }
      
      if (errorMessage.includes('not found')) {
        return {
          status: 'error',
          message: 'File not found',
          error: {
            code: 'file_not_found',
            category: 'execution',
            details: `File "${filePath}" does not exist in your Dropbox. Check the path and try again.`
          },
          format: 'plaintext'
        };
      }
      
      return {
        status: 'error',
        message: 'Failed to read file from Dropbox',
        error: errorMessage,
        format: 'plaintext'
      };
    }
  }
};
